import React, { useState, useEffect, useRef, useCallback } from 'react';
import useMedia from '../../hooks/useMedia';
import { downloadVariantPdf } from '../../utils/variantPdf';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getLesson, submitAnswer, finishLesson, getSections, getHomeworks, getVariants, startRetry } from '../../services/studentService';
import toast from 'react-hot-toast';
import { confirmDialog } from '../../components/Common/confirm';
import { bebasDigits } from '../../utils/bebasDigits';
import Collapse from '../../components/Common/Collapse';
import { slatLayout, slatVars, useRbCollapse, useRbJump } from '../../utils/rbSlats';
import TaskFiles from '../../components/Student/TaskFiles';
import ManualSolution from '../../components/Student/ManualSolution';

/* Новый экран ДЗ (перенос из Claude Design «Домашка ученика»):
   стеклянная карточка фикс-размера, шкала-прогресс сверху, сайдбар «Все номера»,
   закреплённая полоса ответа, интро, результат с разбором-аккордеоном. Адаптив. */

const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const GREEN = '#22c55e';
const ANSW = '#c9c1e0'; // отвечено, но не проверено-верно

// Заголовок номера — единый для всех: просто порядковый «Номер N».
const numLabel = (i) => `Номер ${i + 1}`;
const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const dayLabel = (d) => { const x = d ? new Date(d) : null; return x && !isNaN(x) ? `${x.getDate()} ${MONTHS[x.getMonth()]}` : null; };

const IcBack = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>);
const IcPlay = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20 6 4" /></svg>);
const IcArrow = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>);
const IcChevronL = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>);
const IcChevronD = ({ up }) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={up ? { transform: 'rotate(180deg)' } : undefined}><polyline points="6 9 12 15 18 9" /></svg>);
const IcUp = () => (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>);
const IcRetry = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v6h6" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L3 8" /></svg>);

// Ответ в разборе: у сетки — значения ячеек через пробел, у остальных — как есть
const fmtAns = (h, v) => {
  const s = String(v ?? '').trim();
  if (!s) return '';
  if (h.answerType === 'grid') {
    try { const a = JSON.parse(s); if (Array.isArray(a)) return a.map(x => String(x ?? '').trim()).filter(Boolean).join(' '); } catch { /* не JSON — показываем как есть */ }
  }
  return s;
};

const LessonHomeworkPage = () => {
  const { lessonId: id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Узкий экран: подпись «Ответ» слева от поля скрыта — подсказка в поле короче
  const narrowAns = useMedia('(max-width: 860px)');
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  /* Номера с ручной проверкой: к тексту решения идут файлы и комментарий.
     Держим отдельно от обычных ответов — у тех только строка. */
  const [manual, setManual] = useState({});     // { [hwId]: { answer, comment, files } }
  const [sendingManual, setSendingManual] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [finished, setFinished] = useState(false);
  const [results, setResults] = useState(null);
  const [hwIds, setHwIds] = useState([]);
  const [varIds, setVarIds] = useState([]);
  const [step, setStep] = useState('intro'); // 'intro' | индекс номера
  const [openRows, setOpenRows] = useState({});
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [jumpTo, setJumpTo] = useState(null);   // номер, к которому только что перешли
  const jumpTimer = useRef(null);                // снятие подсветки: перезаводим на каждый клик
  const [retrying, setRetrying] = useState(false); // запускаем повторную попытку
  // Ширина полосы под плитки-номера: по ней считаем, сколько их влезает в ряд.
  const slatRO = useRef(null);
  const [slatBox, setSlatBox] = useState(0);
  const slatsRef = useCallback((el) => {
    slatRO.current?.disconnect();
    if (!el) return;
    setSlatBox(el.clientWidth);
    slatRO.current = new ResizeObserver(([e]) => setSlatBox(e.contentRect.width));
    slatRO.current.observe(el);
  }, []);
  const startAttempt = (at) => setStep(at);
  // Свернуть/развернуть шапку разбора — остаются только плитки-номера
  const rb = useRbCollapse(setSlatBox);

  useEffect(() => {
    setLesson(null); setAnswers({}); setFinished(false); setResults(null); setSubmitting(false); setLoading(true); setStep('intro'); setOpenRows({}); setOnlyErrors(false);
    loadLesson();
  }, [id]);

  useEffect(() => {
    if (loading || finished) return;
    try { localStorage.setItem(`hwdraft:${id}`, JSON.stringify(answers)); } catch (e) {}
  }, [answers, loading, finished, id]);

  useRbJump(jumpTo);   // переход к номеру: см. utils/rbSlats

  // Соседи для кнопки «дальше»: у ДЗ — список заданий курса, у варианта — список
  // вариантов в том же порядке, что видит ученик в разделе «Варианты».
  useEffect(() => {
    Promise.all([getSections(), getHomeworks(), getVariants()]).then(([secRes, hwRes, varRes]) => {
      const hwSet = new Set((hwRes.data || []).map(h => h.lessonId));
      const ordered = (secRes.data || [])
        .flatMap(s => (s.Lessons || []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)))
        .map(l => l.id).filter(lid => hwSet.has(lid));
      setHwIds(ordered);
      setVarIds((varRes.data || []).map(v => v.id));
    }).catch(() => {});
  }, []);

  const loadLesson = async () => {
    try {
      const response = await getLesson(id);
      setLesson(response.data);
      const initial = {};
      (response.data.Homework || []).forEach(hw => { if (hw.answer?.answer) initial[hw.id] = hw.answer.answer; });
      // Идёт повторная попытка — показываем решение, а не разбор засчитанной
      const isCompleted = response.data.progress?.completedAt != null && !response.data.attempt?.active;
      if (isCompleted) {
        setAnswers(initial);
        setResults(calculateResults(response.data.Homework || [], initial));
        try { localStorage.removeItem(`hwdraft:${id}`); } catch (e) {}
      } else {
        let draft = {};
        try { draft = JSON.parse(localStorage.getItem(`hwdraft:${id}`) || '{}'); } catch (e) {}
        setAnswers({ ...initial, ...draft });
      }
      setFinished(isCompleted);
    } catch (error) {
      console.error('Error loading homework:', error);
      toast.error('Ошибка загрузки задания');
    } finally { setLoading(false); }
  };

  const checkTextAnswer = (homework, userAnswer) => {
    if (!homework.correctAnswer) return false;
    const normalize = (s) => s?.trim().toLowerCase().replace(/\s+/g, ' ');
    if (homework.answerType === 'grid') {
      try {
        const correct = JSON.parse(homework.correctAnswer);
        const user = JSON.parse(userAnswer || '[]');
        if (!Array.isArray(correct) || !Array.isArray(user) || correct.length !== user.length) return false;
        return correct.every((v, i) => normalize(String(v)) === normalize(String(user[i] ?? '')));
      } catch { return false; }
    }
    return normalize(userAnswer) === normalize(homework.correctAnswer);
  };

  const getGridValue = (homeworkId, rows, cols) => {
    try { const flat = JSON.parse(answers[homeworkId] || '[]'); return Array.from({ length: rows }, (_, r) => Array.from({ length: cols }, (_, c) => flat[r * cols + c] ?? '')); }
    catch { return Array.from({ length: rows }, () => Array(cols).fill('')); }
  };
  const setGridCell = (homeworkId, row, col, value, rows, cols) => {
    setAnswers(prev => { let flat; try { flat = JSON.parse(prev[homeworkId] || '[]'); } catch { flat = []; } while (flat.length < rows * cols) flat.push(''); flat[row * cols + col] = value; return { ...prev, [homeworkId]: JSON.stringify(flat) }; });
  };
  // Вставка нескольких ответов сразу: «12 34 56» из буфера раскладывается по ячейкам
  // начиная с той, куда вставляют (как в админке при создании номера).
  const pasteGrid = (e, homeworkId, row, col, rows, cols) => {
    const tokens = e.clipboardData.getData('text').split(/\s+/).filter(Boolean);
    if (tokens.length <= 1) return;
    e.preventDefault();
    setAnswers(prev => {
      let flat; try { flat = JSON.parse(prev[homeworkId] || '[]'); } catch { flat = []; }
      while (flat.length < rows * cols) flat.push('');
      let idx = 0;
      for (let p = row * cols + col; p < rows * cols && idx < tokens.length; p++, idx++) flat[p] = tokens[idx];
      return { ...prev, [homeworkId]: JSON.stringify(flat) };
    });
  };

  const calculateResults = (homeworks, currentAnswers) => homeworks.map(hw => {
    const textAnswer = (currentAnswers?.[hw.id] !== undefined) ? currentAnswers[hw.id] : (hw.answer?.answer || '');
    return { id: hw.id, isCorrect: checkTextAnswer(hw, textAnswer) };
  });

  const handleAnswerChange = (homeworkId, value) => { if (!finished) setAnswers(prev => ({ ...prev, [homeworkId]: value })); };

  const hasAnswer = (hw) => {
    // у ручного номера ответа в общей карте нет — он живёт в своём черновике
    if (hw.manualReview) {
      const m = manual[hw.id] || {};
      return !!hw.answer || String(m.answer || '').trim() !== '' || (m.files || []).length > 0;
    }
    const v = answers[hw.id];
    if (v == null) return false;
    if (hw.answerType === 'grid') {
      const need = (hw.gridRows || 3) * (hw.gridCols || 2);
      try { const arr = JSON.parse(v); if (!Array.isArray(arr) || arr.length < need) return false; for (let i = 0; i < need; i++) if (String(arr[i] ?? '').trim() === '') return false; return true; }
      catch { return false; }
    }
    return String(v).trim() !== '';
  };

  // Подтягиваем уже отправленное решение, чтобы ученик видел свой текст и файлы
  useEffect(() => {
    const src = lesson?.Homework || [];
    const next = {};
    src.filter(h => h.manualReview).forEach(h => {
      next[h.id] = {
        answer: h.answer?.answer || '',
        comment: h.answer?.comment || '',
        files: h.answer?.files || [],
      };
    });
    if (Object.keys(next).length) setManual(prev => ({ ...next, ...prev }));
  }, [lesson?.id]); // eslint-disable-line

  const sendManual = async (homeworkId) => {
    const v = manual[homeworkId] || {};
    if (!String(v.answer || '').trim() && !(v.files || []).length) {
      toast.error('Добавь решение или файл');
      return;
    }
    setSendingManual(true);
    try {
      await submitAnswer(homeworkId, { answer: v.answer || '', comment: v.comment || '', files: v.files || [] });
      toast.success('Отправлено на проверку');
      setLesson(prev => prev ? {
        ...prev,
        Homework: prev.Homework.map(hw => hw.id === homeworkId
          ? { ...hw, answer: { ...(hw.answer || {}), answer: v.answer, comment: v.comment, files: v.files, status: 'pending', reviewedAt: null, score: null, reviewComment: null } }
          : hw),
      } : prev);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Не удалось отправить');
    } finally { setSendingManual(false); }
  };

  // Верность ответа проверяет сервер: правильных ответов у ученика до сдачи нет.
  const handleCheckAnswer = async (homeworkId) => {
    if (finished) return;
    const homework = lesson?.Homework?.find(h => h.id === homeworkId);
    if (!hasAnswer(homework)) { toast.error('Введите ответ'); return; }
    const answer = answers[homeworkId];
    try {
      const res = await submitAnswer(homeworkId, { answer });
      const status = res?.data?.status === 'correct' ? 'correct' : 'incorrect';
      if (status === 'correct') toast.success('Верно — засчитано'); else toast.error('Пока неверно', { style: { background: '#ffe5e5', color: '#b91c1c' } });
      setLesson(prev => prev ? { ...prev, Homework: prev.Homework.map(hw => hw.id === homeworkId ? { ...hw, answer: { ...hw.answer, answer, status } } : hw) } : prev);
    } catch { toast.error('Ошибка при сохранении'); }
  };

  const handleFinishLesson = async (allFilled = true) => {
    if (finished) return;
    if (!allFilled) {
      const ok = await confirmDialog({ title: 'Завершить попытку?', message: 'Есть незаполненные номера — они будут засчитаны как неверные. Точно завершить?', confirmText: 'Завершить' });
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      /* Ручные номера при завершении не переотправляем «пустышкой»: иначе
         решение, файлы и уже выставленные баллы затирались бы. Отправляем
         только те, где есть черновик, который ученик не успел отправить. */
      await Promise.all((lesson?.Homework || []).map(hw => {
        if (!hw.manualReview) return submitAnswer(hw.id, { answer: answers[hw.id] || '' });
        const m = manual[hw.id] || {};
        const filled = String(m.answer || '').trim() !== '' || (m.files || []).length > 0;
        const sent = hw.answer;
        const same = sent
          && (sent.answer || '') === (m.answer || '')
          && (sent.comment || '') === (m.comment || '')
          && (sent.files || []).length === (m.files || []).length;
        if (!filled || same) return Promise.resolve();
        return submitAnswer(hw.id, { answer: m.answer || '', comment: m.comment || '', files: m.files || [] });
      }));
      const res = await finishLesson(id);
      try { localStorage.removeItem(`hwdraft:${id}`); } catch (e) {}
      const r = res?.retry;   // завершили повторную попытку — засчитан лучший результат
      if (!r) toast.success('Задание завершено');
      else if (r.improved) toast.success(`Новый лучший результат — ${r.best.correct} из ${r.best.total}`);
      else toast(`Засчитан прежний результат — ${r.best.correct} из ${r.best.total}`, { icon: '📌' });
      loadLesson();
    } catch { toast.error('Ошибка при завершении'); }
    finally { setSubmitting(false); }
  };

  // Повторная попытка от куратора: засчитанный результат остаётся, ученик решает
  // заново, в зачёт пойдёт лучший.
  const handleRetry = async () => {
    setRetrying(true);
    try {
      await startRetry(id);
      try { localStorage.removeItem(`hwdraft:${id}`); } catch (e) {}
      setOpenRows({}); setOnlyErrors(false);
      await loadLesson();
      setStep(0);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Не удалось начать попытку');
    } finally { setRetrying(false); }
  };

  if (loading) return <div className="lesson-full lesson-loading" style={{ background: 'var(--bg)' }}><div className="lesson-spinner" /></div>;
  if (!lesson) return <div className="lesson-full lesson-loading" style={{ background: 'var(--bg)' }}>Задание не найдено</div>;

  const hw = lesson.Homework || [];
  const n = hw.length;
  const isVariant = lesson.isVariant;
  const kind = isVariant ? 'Вариант' : 'Домашнее задание';
  const attempt = lesson.attempt || { extra: 0, active: false };

  const stateOf = (h) => h.answer?.status === 'correct' ? 'correct' : (h.answer?.answer || hasAnswer(h)) ? 'answered' : 'empty';
  const correctCount = hw.filter(h => h.answer?.status === 'correct').length;
  const answeredCount = hw.filter(h => stateOf(h) === 'answered').length;
  const remainingCount = Math.max(0, n - correctCount - answeredCount);
  // номера с ручной проверкой ждут человека — считаем их отдельно, иначе
  // «1 верно из 2» выглядит как ошибка, хотя второй просто ещё не смотрели
  const pendingCount = hw.filter(h => h.answer?.status === 'pending').length;
  const linkTo = (lesson.isHomeworkOnly || isVariant) ? lesson.linkedLessonId : id;

  const goBack = () => navigate(location.state?.from || (isVariant ? '/tests' : '/homework'));
  const nextHwId = () => {
    const list = isVariant ? varIds : hwIds;
    const idx = list.indexOf(Number(id));
    return idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null;
  };
  const nextPath = isVariant ? 'variant' : 'homework';
  const nextLabel = isVariant ? 'К следующему варианту' : 'К следующему ДЗ';

  // ─────────────────── ИНТРО ───────────────────
  if (!finished && step === 'intro') {
    const started = hw.some(h => h.answer) || Object.values(answers).some(v => v && String(v).trim() && String(v).trim() !== '[]');
    const firstUnsolved = hw.findIndex(h => h.answer?.status !== 'correct');
    const startAt = firstUnsolved >= 0 ? firstUnsolved : 0;
    return (
      <div className="lesson-full" style={{ background: 'var(--bg)' }}>
        <div className="nhw-wrap center">
          <div className="nhw-card" style={{ maxWidth: 820 }}>
            <div className="nhw-content" style={{ overflow: 'visible', padding: '28px 48px 42px' }}>
              <button className="nhw-tb" onClick={goBack} title="Назад" style={{ width: 42, height: 42, borderRadius: 12, border: '1px solid var(--lg-edge)', background: 'var(--card)', color: 'var(--text-2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginBottom: 22 }}><IcBack /></button>
              <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.7px', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 14 }}>{kind}</div>
              <div className="nhw-hero" style={{ fontSize: 46, marginBottom: 18 }}>{bebasDigits(lesson.title)}</div>
              <div style={{ fontSize: 16, color: 'var(--text-2)', lineHeight: 1.55, maxWidth: 560 }}>
                {n > 0 ? (started ? `${answeredCount + correctCount} из ${n} номеров уже с ответами. Продолжим с номера ${startAt + 1}.` : `${n} ${n === 1 ? 'номер' : n > 1 && n < 5 ? 'номера' : 'номеров'} — реши каждый и заверши попытку.`) : 'К этому уроку пока нет заданий.'}
              </div>
              {attempt.active && (
                <div className="nhw-retry-note">Повторная попытка · сейчас засчитано {attempt.bestCorrect} из {attempt.total}, в зачёт пойдёт лучший результат</div>
              )}
              {n > 0 && (
                <>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: 14, margin: '30px 0 14px' }}>
                    {hw.map((h) => <span key={h.id} style={{ flex: 1, height: 14, borderRadius: 5, background: stateOf(h) === 'correct' ? GREEN : stateOf(h) === 'answered' ? ANSW : 'var(--border)' }} />)}
                  </div>
                  <div style={{ display: 'flex', gap: 30, fontSize: 15, color: 'var(--text-2)' }}>
                    <span>{answeredCount + correctCount} с ответом</span><span>{correctCount} верно</span>
                    {pendingCount > 0 && <span>{pendingCount} на проверке</span>}
                    <span>{remainingCount} осталось</span>
                  </div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '22px 48px', borderTop: '1px solid var(--lg-edge)', background: 'var(--bg)' }}>
              <button onClick={() => startAttempt(startAt)} disabled={n === 0} style={{ height: 46, padding: '0 24px', border: 'none', borderRadius: 12, background: n === 0 ? 'var(--border)' : 'linear-gradient(150deg,#8b5cf6,#6d28d9)', color: '#fff', fontFamily: 'inherit', fontSize: 14.5, fontWeight: 700, cursor: n === 0 ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: n === 0 ? 'none' : '0 10px 22px -12px rgba(124,77,255,0.9)' }}>
                {started ? `Продолжить с №${startAt + 1}` : (isVariant ? 'Начать вариант' : 'Начать ДЗ')}<IcArrow />
              </button>
              {isVariant && n > 0 && (
                <button onClick={() => downloadVariantPdf(id, lesson.title)}
                  title="Скачать бланк варианта"
                  style={{ height: 46, padding: '0 20px', border: '1px solid var(--lg-edge)', background: 'var(--card)', borderRadius: 12, fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, color: 'var(--text-2)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Скачать PDF
                </button>
              )}
              {linkTo && <button onClick={() => navigate(`/lesson/${linkTo}`, { state: { from: `/homework/${id}` } })} style={{ height: 46, padding: '0 22px', border: 'none', background: 'var(--primary)', borderRadius: 12, fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, color: '#fff', cursor: 'pointer', boxShadow: '0 8px 18px -8px rgba(124,77,255,0.8)' }}>К вебинару</button>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────── РЕЗУЛЬТАТ + РАЗБОР ───────────────────
  if (finished) {
    /* У ручного номера своего «правильного ответа» нет — верность решает куратор.
       Пока он не посмотрел, номер не ошибка и не успех, а «на проверке». */
    const graded = hw.map((h, i) => {
      const ua = answers[h.id] !== undefined ? answers[h.id] : (h.answer?.answer || '');
      const manual = !!h.manualReview;
      const st = h.answer?.status;
      return {
        h, i, user: ua, manual,
        waiting: manual && st === 'pending',
        ok: manual ? st === 'correct' : checkTextAnswer(h, ua),
      };
    });
    const total = graded.length;
    const correct = graded.filter(g => g.ok).length;
    const waitingCount = graded.filter(g => g.waiting).length;
    const pct = total ? Math.round((correct / total) * 100) : 0;
    const shown = onlyErrors ? graded.filter(g => !g.ok) : graded;
    const vs = isVariant ? lesson.variantScore : null;
    const varScore = !vs ? null
      : vs.examType === 'ОГЭ'
        ? (vs.grade != null ? { value: vs.grade, label: 'оценка', max: 5 } : null)
        : (vs.secondary != null ? { value: vs.secondary, label: 'вт. баллов', max: 100 } : null);
    // Клик по квадратику: снимаем фильтр, раскрываем номер и мягко подводим
    // его карточку к центру — как будто листаешь сам.
    const openNumber = (g) => {
      setOnlyErrors(false);
      setOpenRows(p => ({ ...p, [g.h.id]: true }));
      setJumpTo(g.h.id);          // сам переход — в эффекте выше, после отрисовки
      clearTimeout(jumpTimer.current);
      jumpTimer.current = setTimeout(() => setJumpTo(null), 900);
    };
    return (
      <div className="lesson-full" style={{ background: 'var(--bg)' }}>
        <div className="nhw-wrap">
          <div className="nhw-card fixed" style={{ maxWidth: 'none' }}>
            {/* ── Баннер результата (дизайн 1b) ── */}
            <div className={`rb-hero${rb.min ? ' min' : ''}${rb.fade ? ' fade' : ''}`} ref={rb.heroRef}>
              {/* Свернуть шапку: останутся плитки-номера, чтобы переключаться по разбору */}
              <button type="button" className="rb-toggle" onClick={rb.toggle} aria-expanded={!rb.min}
                title={rb.min ? 'Развернуть' : 'Свернуть'} aria-label={rb.min ? 'Развернуть шапку' : 'Свернуть шапку'}>
                <IcUp />
              </button>
              <div className="rb-top">
                <button className="rb-back" onClick={goBack} title="Назад"><IcBack /></button>
                <span className="rb-crumb">{lesson.title}</span>
                {dayLabel(lesson.date) && <><span className="rb-sep" /><span className="rb-crumb dim">{dayLabel(lesson.date)}</span></>}
              </div>

              <div className="rb-main">
                <div className="rb-score">
                  {/* У варианта вместо процента — балл, как на его карточке в «Вариантах»:
                      вторичный у ЕГЭ, оценка у ОГЭ; подпись мелко справа */}
                  {waitingCount > 0 ? (
                    <>
                      <div className="rb-pct rb-pct-wait">На проверке</div>
                      <div className="rb-of dim">Финальный процент будет виден,<br />когда куратор проверит решение</div>
                    </>
                  ) : varScore
                    ? <>
                      <div className="rb-pct rb-points">{varScore.value}<span className="rb-points-l"><span>{varScore.label}</span><span>из <b className="rb-points-num">{varScore.max}</b></span></span></div>
                      <div className="rb-of"><b>{correct}</b> из <b>{total}</b> верно</div>
                    </>
                    : <>
                      <div className="rb-pct">{pct}%</div>
                      <div className="rb-of"><b>{correct}</b> из <b>{total}</b> верно</div>
                    </>}
                </div>

                <div className="rb-right">
                  {/* Плитки «№N»: клик — плавный переход к разбору номера.
                      Размер плитки одинаков всегда, меняется только их число в ряду. */}
                  <div className="rb-slats" ref={slatsRef} style={slatVars(slatLayout(total, slatBox))}>
                    {graded.map((g) => (
                      <button key={g.h.id} type="button"
                        className={`rb-slat${g.ok ? ' ok' : ''}${jumpTo === g.h.id ? ' hit' : ''}`}
                        title={`№${g.i + 1} — ${g.ok ? 'верно' : 'с ошибкой'}`}
                        onClick={() => openNumber(g)}>
                        <span className="rb-slat-n">№{g.i + 1}</span>
                        <span className="rb-slat-sq" />
                      </button>
                    ))}
                  </div>

                  <div className="rb-acts">
                    {total - correct > 0 && (
                      <button className="rb-fix" onClick={() => navigate('/mistakes', { state: { from: `/homework/${id}` } })}>
                        Разобрать ошибки
                      </button>
                    )}
                    <button className="rb-next" onClick={() => { const nid2 = nextHwId(); nid2 ? navigate(`/${nextPath}/${nid2}`, { state: { from: location.state?.from || (isVariant ? '/tests' : '/homework') } }) : goBack(); }}>
                      {nextHwId() ? nextLabel : 'Готово'}
                    </button>
                    {attempt.extra > 0 && (
                      <>
                        <button className="rb-next rb-retry" onClick={handleRetry} disabled={retrying}>
                          <IcRetry />{retrying ? 'Запускаем…' : 'Пройти ещё раз'}
                        </button>
                        <span className="rb-note">засчитается лучший результат</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Панель разбора ── */}
            <div className="rb-bar">
              <span className="rb-bar-t">Разбор ответов</span>
              <button className={`rb-chip${!onlyErrors ? ' on' : ''}`} onClick={() => setOnlyErrors(false)}>Все {total}</button>
              <button className={`rb-chip${onlyErrors ? ' on' : ''}`} onClick={() => setOnlyErrors(true)}>Только ошибки</button>
              <span style={{ flex: 1 }} />
              <span className="rb-bar-hint">нажмите номер, чтобы увидеть решение</span>
            </div>

            <div className="rb-list">
              {shown.map((g) => {
                const open = !!openRows[g.h.id];
                const files = g.h.attachedSolutionFiles || [];
                const hasSolution = g.h.textSolution || g.h.videoSolution || files.length > 0;
                return (
                  <div key={g.h.id} id={`rb-row-${g.h.id}`}
                    className={`rb-card${open ? ' open' : ''}${jumpTo === g.h.id ? ' hit' : ''}`}>
                    <button className="rb-head" onClick={() => setOpenRows(p => ({ ...p, [g.h.id]: !p[g.h.id] }))}>
                      <span className={`rb-mark${g.waiting ? ' wait' : (g.ok ? ' ok' : ' bad')}`}>
                        {g.waiting
                          ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>
                          : g.ok
                            ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                            : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>}
                      </span>
                      <span className="rb-no">№{g.i + 1}</span>
                      <span className="rb-title">{numLabel(g.i)}</span>
                      {g.manual ? (
                        <span className="rb-manual">
                          {g.waiting ? 'решение на проверке'
                            : g.h.answer?.score != null ? `${g.h.answer.score} из ${g.h.maxScore ?? 1}`
                              : (g.ok ? 'зачтено' : 'не зачтено')}
                        </span>
                      ) : (
                        <>
                          {g.h.answerType !== 'grid' && (
                            <span className="rb-user" style={{ opacity: g.ok || !String(g.user || '').trim() ? 0 : 1 }}>{g.user}</span>
                          )}
                          {g.h.answerType !== 'grid' && <span className="rb-ans">{g.h.correctAnswer || '—'}</span>}
                        </>
                      )}
                      <span className="rb-chev"><IcChevronD up={open} /></span>
                    </button>

                    <Collapse open={open}>
                      <div className="rb-body">
                        <div className="rb-col">
                          <div className="rb-cap">Условие</div>
                          {g.h.description
                            ? <div className="hw-rich rb-text" dangerouslySetInnerHTML={{ __html: g.h.description }} />
                            : <div className="rb-text dim">Условие не заполнено</div>}
                          <TaskFiles files={g.h.attachedFiles} />
                          {g.manual ? (
                            /* Эталона нет: показываем то, что ученик отправил, и вердикт куратора */
                            <div className="rb-manual-box">
                              <div className={`rb-verdict${g.waiting ? ' wait' : (g.ok ? ' ok' : ' no')}`}>
                                {g.waiting ? 'Решение отправлено на проверку'
                                  : (g.ok ? 'Зачтено' : 'Не зачтено')}
                                {!g.waiting && g.h.answer?.score != null && (
                                  <span className="rb-verdict-score">{g.h.answer.score} из {g.h.maxScore ?? 1}</span>
                                )}
                              </div>
                              <span className="rb-cap">Ваше решение</span>
                              {String(g.h.answer?.answer || '').trim()
                                ? <div className="rb-solution">{g.h.answer.answer}</div>
                                : <div className="rb-text dim">Текста нет — только файлы</div>}
                              <TaskFiles files={g.h.answer?.files} />
                            </div>
                          ) : (
                            /* Верно — одно поле, ответ зелёным. Ошибка — рядом правильный ответ */
                            <div className="rb-answers">
                              <div className="rb-yours">
                                <span className="rb-cap">Ваш ответ</span>
                                <span className={`rb-yours-v${g.ok ? ' ok' : ''}`}>{fmtAns(g.h, g.user) || 'нет ответа'}</span>
                              </div>
                              {!g.ok && (
                                <div className="rb-yours">
                                  <span className="rb-cap">Правильный ответ</span>
                                  <span className="rb-yours-v ok">{fmtAns(g.h, g.h.correctAnswer) || '—'}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="rb-col right">
                          <div className="rb-cap accent">{g.manual ? 'Куратор' : 'Решение'}</div>
                          {g.manual
                            ? (g.h.answer?.reviewComment
                              ? <div className="rb-review">{g.h.answer.reviewComment}</div>
                              : <div className="rb-text dim">{g.waiting ? 'Решение ещё не посмотрели — вердикт придёт уведомлением' : 'Куратор проверил без комментария'}</div>)
                            : g.h.textSolution
                              ? <div className="hw-rich rb-text" dangerouslySetInnerHTML={{ __html: g.h.textSolution }} />
                              : <div className="rb-text dim">Разбор пока не добавлен</div>}
                          {g.h.videoSolution && (
                            <div className="rb-solve-acts">
                              <a className="rb-video" href={g.h.videoSolution} target="_blank" rel="noopener noreferrer">
                                <span className="rb-video-ic"><IcPlay /></span>Видеоразбор
                              </a>
                            </div>
                          )}
                          <TaskFiles files={files} />
                          {!hasSolution && !g.h.videoSolution && null}
                        </div>
                      </div>
                    </Collapse>
                  </div>
                );
              })}
              {/* Распорка: чтобы последние номера тоже могли встать под верх экрана */}
              <div className="rb-tail" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────── РЕШЕНИЕ НОМЕРА ───────────────────
  const cur = hw[step];
  // У ручного номера свой вердикт с баллами внутри карточки — зелёная полоса
  // «Ответ верный» там только дублировала бы его и путала с автопроверкой
  const curChecked = cur?.answer?.status === 'correct' && !cur?.manualReview;
  const isGrid = cur?.answerType === 'grid';
  const isManual = !!cur?.manualReview;        // у такого номера поля «Ответ» нет
  const isLast = step === n - 1;
  const allFilled = hw.every(hasAnswer);

  return (
    <div className="lesson-full" style={{ background: 'var(--bg)' }}>
      <div className="nhw-wrap">
        <div className="nhw-card fixed">
          {/* header */}
          <div className="nhw-head">
            <button className="nhw-tb" onClick={goBack} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', border: '1px solid var(--lg-edge)', borderRadius: 10, background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--text-2)' }}><IcBack /> Назад</button>
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* в шапке — сразу название урока: тип задания и так виден по содержимому */}
              <div style={{ fontSize: 15.5, fontWeight: 700, letterSpacing: '-0.2px', color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lesson.title}</div>
            </div>
            {linkTo && <button onClick={() => navigate(`/lesson/${linkTo}`, { state: { from: `/homework/${id}` } })} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 18px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 30, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 6px 16px -6px rgba(124,77,255,0.85)' }}><IcPlay /> К вебинару</button>}
          </div>

          {/* шкала-прогресс */}
          <div className="nhw-scale">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 11, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontFamily: MONO, fontSize: 15, fontWeight: 700, color: 'var(--text-1)' }}>№{step + 1}</span>
                <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>из {n}</span>
              </div>
              <div style={{ flex: 1 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12, color: 'var(--text-2)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: GREEN }} />верно {correctCount}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: ANSW }} />отвечено {answeredCount}</span>
                {pendingCount > 0 && (
                  <span className="nhw-hide-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--primary)' }} />на проверке {pendingCount}</span>
                )}
                <span className="nhw-hide-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--border)' }} />осталось {remainingCount}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 5, alignItems: 'flex-end', height: 30 }}>
              {hw.map((h, i) => {
                const isCur = i === step;
                const bg = isCur ? 'var(--primary)' : stateOf(h) === 'correct' ? GREEN : stateOf(h) === 'answered' ? ANSW : 'var(--border)';
                return (
                  <button key={h.id} onClick={() => setStep(i)} title={`№${i + 1}`} style={{ flex: 1, height: isCur ? 30 : 10, minWidth: 0, border: 'none', borderRadius: isCur ? 6 : 4, background: bg, cursor: 'pointer', padding: 0, position: 'relative', boxShadow: isCur ? '0 6px 16px -6px rgba(124,77,255,0.8)' : 'none' }}>
                    {isCur && <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontSize: 12, fontWeight: 700, color: '#fff' }}>{i + 1}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* контент + сайдбар */}
          <div className="nhw-body">
            <div className="nhw-content">
              <div className="nhw-hero" style={{ fontSize: 30, marginBottom: 16 }}>{bebasDigits(numLabel(step))}</div>
              {cur?.description && <div className="hw-rich" style={{ color: 'var(--text-2)', maxWidth: 640, whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: cur.description }} />}

              {/* Файлы к условию — такими же строками, как у админа в редакторе */}
              <TaskFiles files={cur?.attachedFiles} />

              {cur?.manualReview && (
                <ManualSolution
                  homework={cur}
                  value={manual[cur.id] || { answer: '', comment: '', files: [] }}
                  onChange={(v) => setManual(m => ({ ...m, [cur.id]: v }))}
                  onSubmit={() => sendManual(cur.id)}
                  sending={sendingManual}
                  readOnly={finished}
                />
              )}

              {isGrid && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ overflowX: 'auto', marginBottom: 10 }}>
                    <table style={{ borderCollapse: 'collapse' }}><tbody>
                      {getGridValue(cur.id, cur.gridRows || 3, cur.gridCols || 2).map((row, r) => (
                        <tr key={r}>{row.map((cell, c) => (
                          <td key={c} className="nhw-gcell">
                            <input type="text" value={cell}
                              onChange={e => setGridCell(cur.id, r, c, e.target.value, cur.gridRows || 3, cur.gridCols || 2)}
                              onPaste={e => pasteGrid(e, cur.id, r, c, cur.gridRows || 3, cur.gridCols || 2)} />
                          </td>
                        ))}</tr>
                      ))}
                    </tbody></table>
                  </div>
                  <button className="nhw-check" onClick={() => handleCheckAnswer(cur.id)} style={{ height: 40, padding: '0 20px', border: 'none', borderRadius: 10, background: 'var(--primary-light)', color: 'var(--primary-dark)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Проверить</button>
                </div>
              )}
            </div>

            {/* сайдбар: все номера */}
            <div className="nhw-side">
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-3)' }}>Все номера</div>
              <div className="nhw-numlist">
                {hw.map((h, i) => {
                  const st = stateOf(h); const isCur = i === step;
                  const dot = isCur ? 'var(--primary)' : st === 'correct' ? GREEN : st === 'answered' ? ANSW : 'var(--border)';
                  return (
                    <button key={h.id} className={`nhw-numrow${isCur ? ' cur' : ''}`} onClick={() => setStep(i)}>
                      {/* значок-квадрат с номером — как у админа в панели номеров */}
                      <span className="nhw-numbadge">{i + 1}</span>
                      <span style={{ flex: 1, fontSize: 13.5, fontWeight: isCur ? 700 : 500, color: isCur ? 'var(--text-1)' : 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{numLabel(i)}</span>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot, flexShrink: 0 }} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* закреплённая полоса ответа */}
          <div className="nhw-bottom">
            <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} className="nhw-hit" style={{ width: 42, height: 46, border: '1px solid var(--lg-edge)', background: 'var(--card)', borderRadius: 12, cursor: step === 0 ? 'default' : 'pointer', color: 'var(--text-2)', opacity: step === 0 ? 0.4 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><IcChevronL /></button>
            {isManual && (
              <div className="nhw-ansbox manual" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, height: 46, padding: '0 18px', border: '1.5px solid var(--lg-edge)', borderRadius: 13, background: 'var(--bg)', minWidth: 0 }}>
                <span style={{ fontSize: 13.5, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {cur?.answer?.status === 'pending' ? 'Решение отправлено — ждёт куратора'
                    : cur?.answer?.reviewedAt ? `Проверено: ${cur.answer.score ?? 0} из ${cur.maxScore ?? 1}`
                      : 'Решение отправляется кнопкой выше'}
                </span>
              </div>
            )}
            {!isGrid && !isManual && (
              <div className="nhw-ansbox" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, height: 46, padding: '0 6px 0 18px', border: '1.5px solid var(--lg-edge)', borderRadius: 13, background: 'var(--card)', minWidth: 0 }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 9 }}>
                  <span className="nhw-hide-sm" style={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '0.3px', textTransform: 'uppercase', color: 'var(--text-3)', flexShrink: 0 }}>Ответ</span>
                  <input className="nhw-ans-input" value={answers[cur.id] || ''} onChange={e => handleAnswerChange(cur.id, e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleCheckAnswer(cur.id); }} placeholder={narrowAns ? 'Ответ' : 'введите значение'} style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'none', fontFamily: MONO, fontSize: 16, fontWeight: 700, color: 'var(--text-1)' }} />
                </div>
                <button className="nhw-check" onClick={() => handleCheckAnswer(cur.id)} style={{ height: 34, padding: '0 18px', border: 'none', borderRadius: 9, background: 'var(--primary-light)', color: 'var(--primary-dark)', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>Проверить</button>
              </div>
            )}
            {isGrid && !isManual && (
              <div className="nhw-ansbox grid" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, height: 46, padding: '0 18px', border: '1.5px solid var(--lg-edge)', borderRadius: 13, background: 'var(--bg)', minWidth: 0 }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 9 }}>
                  <span className="nhw-hide-sm" style={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '0.3px', textTransform: 'uppercase', color: 'var(--text-3)', flexShrink: 0 }}>Ответ</span>
                  <span style={{ fontSize: 13.5, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>необходимо внести в сетку ответов</span>
                </div>
              </div>
            )}
            <button className="nhw-next" onClick={() => isLast ? handleFinishLesson(allFilled) : setStep(s => Math.min(n - 1, s + 1))} disabled={submitting} style={{ height: 46, padding: '0 26px', border: 'none', borderRadius: 13, background: 'linear-gradient(150deg,#8b5cf6,#6d28d9)', color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 9, boxShadow: '0 10px 24px -12px rgba(124,77,255,0.9)', flexShrink: 0, opacity: submitting ? 0.6 : 1 }}>
              {isLast ? (submitting ? 'Отправка…' : 'Завершить') : 'Далее'}{!isLast && <IcArrow />}
            </button>
          </div>

          {curChecked && (
            <div className="nhw-verdict" style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '12px 22px', background: 'rgba(34,197,94,0.08)', borderTop: '1px solid rgba(34,197,94,0.2)', flexShrink: 0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              <span style={{ fontSize: 13, fontWeight: 700, color: GREEN }}>Ответ верный — засчитан</span>
              <span className="nhw-hide-sm" style={{ fontSize: 12.5, color: 'var(--text-2)' }}>разбор откроется после завершения ДЗ</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LessonHomeworkPage;
